import React, { MutableRefObject, useEffect, useState } from 'react';
import { Avatar, CircularProgress } from '@mui/material';
import { IParticipant } from '../../../../../interfaces/participants';
import { useStyles } from './styles';
import { useZoom } from '../Zoom/ZoomProvider';
import { Box } from '@mui/system';
import { COLORS } from '../../../../../constants/colors';

type ParticipantAvatarProps = {
  participant: IParticipant | undefined;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
};

const ParticipantAvatar: React.FC<ParticipantAvatarProps> = ({
  participant,
  videoRef,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideoStream, setHasVideoStream] = useState(false);

  const classes = useStyles();
  const { handleToggleVideoSharing, isCameraOn, isJoiningConference } =
    useZoom();

  useEffect(() => {
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    videoRef.current?.addEventListener('play', handlePlay);
    videoRef.current?.addEventListener('pause', handlePause);

    return () => {
      videoRef.current?.removeEventListener('play', handlePlay);
      videoRef.current?.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current?.srcObject) {
      setHasVideoStream(true);
    } else {
      setHasVideoStream(false);
    }
  }, [videoRef.current?.srcObject, isCameraOn]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          ...classes.videoContainer,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          visibility: isCameraOn && hasVideoStream ? 'visible' : 'hidden',
        }}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          handleToggleVideoSharing();
        }}
      >
        <video
          ref={videoRef}
          playsInline
          autoPlay
          muted
          controls={false}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            transform: 'rotateY(180deg)',
            objectFit: 'cover',
            opacity: isJoiningConference ? 0.5 : 1,
          }}
        />
        {isJoiningConference && (
          <CircularProgress
            size={32}
            sx={{
              position: 'absolute',
              color: COLORS.WHITE,
            }}
          />
        )}
      </Box>
      <Avatar
        src={!hasVideoStream ? participant?.avatarImage : ''}
        alt=""
        sx={{
          ...classes.avatar,
          position: 'absolute',
          top: 0,
          left: 0,
          display: isCameraOn && hasVideoStream ? 'none' : 'flex',
        }}
        onClick={() => handleToggleVideoSharing()}
      >
        {!isCameraOn || !hasVideoStream ? participant?.avatarString : null}{' '}
      </Avatar>
    </Box>
  );
};

export default ParticipantAvatar;
